import { Command } from 'commander';
import { confirm, input } from '@inquirer/prompts';
import { ConnectContextService } from '../../../services/connect-context.service.js';
import { ConnectPolicyService } from '../../../services/connect-policy.service.js';
import { ConnectExecService } from '../../../services/connect-exec.service.js';
import { assertEnvName } from '../shared.js';

interface DeleteOptions {
    confirmEnv?: string;
    reason?: string;
    json?: boolean;
}

export function createConnectCacheDeleteCommand(): Command {
    return new Command('del')
        .description('Delete cache key')
        .argument('<env>', 'Environment')
        .argument('<connection>', 'Connection name (redis|valkey)')
        .argument('<key>', 'Cache key')
        .option('--reason <text>', 'Reason for delete operation')
        .option('--confirm-env <name>', 'Explicit env confirmation')
        .option('-j, --json', 'Output JSON')
        .action(async function (this: Command, envRaw: string, connectionName: string, key: string, options: DeleteOptions) {
            const env = assertEnvName(envRaw);
            const useJson = !!(options.json || this.optsWithGlobals().json);
            const context = new ConnectContextService();
            const settings = context.getSettings();
            const connection = context.getConnection(env, connectionName);
            if (!connection) throw new Error(`Connection '${connectionName}' not found in ${env}`);
            if (!['redis', 'valkey'].includes(connection.runtime)) {
                throw new Error(`Connection '${connection.name}' is not a cache runtime`);
            }

            const policy = new ConnectPolicyService(settings).evaluate(env, connection.runtime, 'delete');
            if (!policy.allowed) throw new Error(`Permission denied: ${policy.reason}`);

            await ensureMutationConfirmed(env, 'delete', policy.requireConfirm || env !== 'local', policy.requireReason || env !== 'local', options.confirmEnv, options.reason);

            const output = new ConnectExecService().runCacheDelete(connection, key).trimEnd();
            if (useJson) {
                console.log(JSON.stringify({ env, connection: connection.name, key, result: output }, null, 2));
                return;
            }
            console.log(output);
        });
}

async function ensureMutationConfirmed(
    env: string,
    operation: string,
    requireConfirm: boolean,
    requireReason: boolean,
    confirmEnv?: string,
    reason?: string
): Promise<void> {
    if (requireReason && !reason) {
        if (process.stdin.isTTY && process.stdout.isTTY) {
            reason = await input({ message: `Reason for ${operation} on ${env}:` });
        }
        if (!reason) throw new Error('Reason is required. Use --reason "<text>".');
    }

    if (!requireConfirm) return;
    if (confirmEnv === env) return;

    if (process.stdin.isTTY && process.stdout.isTTY) {
        const ok = await confirm({ message: `Confirm ${operation} on ${env}?`, default: false });
        if (!ok) throw new Error('Cancelled by user');
        return;
    }

    throw new Error(`Confirmation required. Re-run with --confirm-env ${env}`);
}
